import { ActionType, createReducer } from 'typesafe-actions';

import { DerivativesGroupType, DerivativesOrderType } from 'common/api/autogenerated/trading-gateway';

import { syncPricesChanges } from 'components/AssetsList/redux/assets.mapper';

import * as derivativesActions from './derivatives.actions';
import { initialState } from './derivatives.initialState';
import {
  applyTrendToDerivative,
  mapClosedPositionFromServer,
  mapClosedPositionsFromServer,
  mapDerivativeOrderBookSnapshot,
  mapDerivativeOrderBookUpdate,
  mapDerivativesFromServer,
  mapDerivativesGroupsFromServer,
  mapFavoritesFromServer,
  mapFuturesOrdersHistoryFromServer,
  mapOpenPositionsFromServer,
  mapOpenedPositionsFromServer,
} from './derivatives.mapper';

type State = typeof initialState;
type Actions = ActionType<typeof derivativesActions>;

export const derivativesReducer = createReducer<State, Actions>(initialState)
  .handleAction(derivativesActions.actionGetDerivatives, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      pairs: state.loading.pairsInitial,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivatives, (state, action) => ({
    ...state,
    loading: {
      ...state.loading,
      pairs: false,
      pairsInitial: false,
    },
    derivatives: {
      ...mapDerivativesFromServer({ ...state.derivatives }, action.payload.data || []),
    },
  }))
  .handleAction(derivativesActions.actionErrorGetDerivatives, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      pairs: false,
    },
  }))
  .handleAction(derivativesActions.actionGetDerivativeInfo, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      info: true,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativeInfo, (state, action) => {
    const { statistic, pricesChanges, sentimentDto: sentiment } = action.payload;
    const code = statistic?.code || '';

    return {
      ...state,
      loading: {
        ...state.loading,
        info: false,
      },
      derivatives: {
        ...state.derivatives,
        [code]: {
          ...state.derivatives[code],
          fundingDate: statistic?.fundingDate,
          fundingRate: statistic?.fundingRate,
          statistic,
          sentiment,
          pricesChanges,
        },
      },
    };
  })
  .handleAction(derivativesActions.actionGetDerivativesGroups, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      groups: state.loading.groupsInitial,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativesGroups, (state, action) => ({
    ...state,
    loading: {
      ...state.loading,
      groups: false,
      groupsInitial: false,
    },
    groups: mapDerivativesGroupsFromServer(action.payload.data || []),
  }))
  .handleAction(derivativesActions.actionErrorGetDerivativesGroups, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      groups: false,
    },
  }))
  .handleAction(derivativesActions.actionGetFavoriteDerivatives, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      favorites: state.loading.favoritesInitial,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetFavoriteDerivatives, (state, action) => {
    const symbols = mapFavoritesFromServer(action.payload.data);
    return {
      ...state,
      loading: {
        ...state.loading,
        favorites: false,
        favoritesInitial: false,
      },
      favorites: symbols,
      groups: {
        ...state.groups,
        Favorites: {
          ...state.groups.Favorites,
          type: DerivativesGroupType.FAVORITES,
          symbols,
        },
      },
    };
  })
  .handleAction(derivativesActions.actionReceiveDerivativeMarkPriceUpdateSuccess, (state, action) => {
    const assetFromStream = action.payload;

    const derivatives = { ...state.derivatives };

    const assetFromState = { ...derivatives[assetFromStream.code] };

    return {
      ...state,
      derivatives: {
        ...derivatives,
        [assetFromStream.code]: {
          ...assetFromState,
          prevMarkPrice: assetFromState.markPrice,
          markPrice: assetFromStream.price,
        },
      },
    };
  })
  .handleAction(derivativesActions.actionReceiveDerivativePriceUpdateSuccess, (state, action) => {
    const assetFromStream = action.payload;

    const derivatives = { ...state.derivatives };

    const assetFromState = { ...derivatives[assetFromStream.code] };

    const asset = {
      ...assetFromState,
      price: action.payload.lastTradePrice,
      prevAsk: assetFromState.bestAskPrice,
      bestAskPrice: action.payload.bestAskPrice,
      prevBid: assetFromState.bestBidPrice,
      bestBidPrice: action.payload.bestBidPrice,
      prevPrice: assetFromState.price,
      bestPricePrice: action.payload.lastTradePrice,
      pricesChanges: syncPricesChanges(action.payload.lastTradePrice, assetFromState.pricesChanges),
    };

    return {
      ...state,
      derivatives: {
        ...derivatives,
        [assetFromStream.code]: asset,
      },
    };
  })
  .handleAction(derivativesActions.actionErrorGetFavoriteDerivatives, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      favorites: false,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativesTrend, (state, action) => ({
    ...state,
    derivatives: applyTrendToDerivative(action.payload, {
      ...state.derivatives,
    }),
  }))
  .handleAction(derivativesActions.actionReceiveDerivativeOrderBookSnapshot, (state, action) => {
    const { tradingPairCode } = action.payload;
    const asset = { ...state.derivatives[tradingPairCode] };
    const orderbook = {
      ...mapDerivativeOrderBookSnapshot(action.payload, asset),
    };

    return {
      ...state,
      orderbook: {
        ...state.orderbook,
        [tradingPairCode]: orderbook,
      },
    };
  })
  .handleAction(derivativesActions.actionReceiveDerivativeOrderBookUpdate, (state, action) => {
    const { tradingPairCode } = action.payload;

    const derivatives = { ...state.derivatives };

    const asset = { ...derivatives[tradingPairCode] };

    const orderbook = mapDerivativeOrderBookUpdate(state.orderbook[tradingPairCode], action.payload, { ...asset });

    const isInvalidUpdate =
      state.orderbook[tradingPairCode].version &&
      action.payload.version !== state.orderbook[tradingPairCode].version + 1;

    if (isInvalidUpdate || window._pauseOrderBookUpdate)
      return {
        ...state,
        orderbook: {
          ...state.orderbook,
          [tradingPairCode]: {
            ...state.orderbook[tradingPairCode],
            version: action.payload.version,
          },
        },
      };

    return {
      ...state,
      orderbook: {
        ...state.orderbook,
        [tradingPairCode]: orderbook,
      },
    };
  })
  .handleAction(derivativesActions.actionGetDerivativesOrders, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      ordersInitial: state.loading.ordersInitial,
      orders: true,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativesOrders, (state, action) => ({
    ...state,
    orders: action.payload,
    loading: {
      ...state.loading,
      orders: false,
      ordersInitial: false,
    },
  }))
  .handleAction(derivativesActions.actionGetDerivativesClosedOrders, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      ordersClosedInitial: state.loading.ordersClosedInitial,
      ordersClosed: true,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativesClosedOrders, (state, action) => ({
    ...state,
    closedOrders: mapFuturesOrdersHistoryFromServer([...state.closedOrders, ...action.payload]),
    latestClosedOrder: action.payload[0] || null,
    loading: {
      ...state.loading,
      ordersClosed: false,
      ordersClosedInitial: false,
    },
  }))
  .handleAction(derivativesActions.actionSuccessLatestDerivativeClosedOrder, (state, action) => ({
    ...state,
    latestClosedOrder: action.payload || null,
  }))
  .handleAction(derivativesActions.actionGetDerivativesPositions, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      positions: true,
      positionsInitial: state.loading.positionsInitial,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativesPositions, (state, action) => ({
    ...state,
    positions: mapOpenedPositionsFromServer(action.payload),
    loading: {
      ...state.loading,
      positions: false,
      positionsInitial: false,
    },
  }))
  .handleAction(derivativesActions.actionGetDerivativesHistory, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      history: true,
      historyInitial: state.loading.historyInitial,
    },
  }))
  .handleAction(derivativesActions.actionSuccessGetDerivativesHistory, (state, action) => ({
    ...state,
    history: mapClosedPositionsFromServer(action.payload),
    loading: {
      ...state.loading,
      history: false,
      historyInitial: false,
    },
  }))
  .handleAction(derivativesActions.actionReceiveDerivativePositionCreatedSuccess, (state, action) => ({
    ...state,
    positions: [...state.positions, mapOpenPositionsFromServer(action.payload)],
  }))
  .handleAction(derivativesActions.actionReceiveDerivativePositionUpdatedSuccess, (state, action) => ({
    ...state,
    positions: state.positions.map((position) => {
      if (position.id && action.payload[position.id])
        return mapOpenPositionsFromServer({
          ...position,
          ...action.payload[position.id],
        });

      return position;
    }),
  }))
  .handleAction(derivativesActions.actionReceiveDerivativeClosedPositionCreated, (state, action) => ({
    ...state,
    history: [mapClosedPositionFromServer(action.payload), ...state.history],
  }))
  .handleAction(derivativesActions.actionReceiveDerivativeClosedPositionUpdated, (state, action) => ({
    ...state,
    history: state.history.map((history) =>
      action.payload.id === history.id ? mapClosedPositionFromServer({ ...history, ...action.payload }) : history,
    ),
  }))
  .handleAction(derivativesActions.actionReceiveDerivativePositionClosedSuccess, (state, action) => ({
    ...state,
    positions: state.positions.filter((i) => i.id !== action.payload.id),
  }))
  .handleAction(derivativesActions.actionReceiveDerivativeOrderOpenedSuccess, (state, action) => {
    const orders = [...state.orders];

    const order = orders.find((i) => i.id === action.payload.id);

    const isMarketOrder = action.payload.type === DerivativesOrderType.MARKET;

    if (order || isMarketOrder) return state;

    return {
      ...state,
      orders: [...state.orders, action.payload],
    };
  })
  .handleAction(derivativesActions.actionReceiveDerivativeOrderClosed, (state, action) => ({
    ...state,
    orders: state.orders.filter((i) => i.id !== action.payload.id),
  }))
  .handleAction(derivativesActions.actionReceiveDerivativeOrderUpdated, (state, action) => {
    const stateOrders = [...state.orders];

    const updatedOrders = stateOrders.map((order) => {
      if (order.id === action.payload.id) return { ...order, ...action.payload };

      return order;
    });

    return {
      ...state,
      orders: updatedOrders,
    };
  })
  .handleAction(derivativesActions.actionLoadingGetDerivativesInUseOrders, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      inUseTransactions: true,
    },
  }))
  .handleAction(derivativesActions.actionGetDerivativesInUseOrders, (state, action) => ({
    ...state,
    inUseTransactions: {
      ...state.inUseTransactions,

      // todo, temporary fix, ask backend to add `settlementCurrency` into SIGNALR_INCOME/derivatives-opened-position-closed
      lastSettlementCurrency:
        action.payload.length > 0 ? action.payload[action.payload.length - 1]?.settlementCurrency || '' : '',

      orders: action.payload,
    },
    loading: {
      ...state.loading,
      inUseTransactions: false,
    },
  }))
  .handleAction(derivativesActions.actionErrorGetDerivativesInUseOrders, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      inUseTransactions: false,
    },
  }))
  .handleAction(derivativesActions.actionLoadingGetDerivativesInUsePositions, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      inUseTransactions: true,
    },
  }))
  .handleAction(derivativesActions.actionGetDerivativesInUsePositions, (state, action) => ({
    ...state,
    inUseTransactions: {
      ...state.inUseTransactions,

      // todo, temporary fix, ask backend to add `settlementCurrency` into SIGNALR_INCOME/derivatives-opened-position-closed
      lastSettlementCurrency:
        action.payload.length > 0 ? action.payload[action.payload.length - 1]?.settlementCurrency || '' : '',

      positions: action.payload,
    },
    loading: {
      ...state.loading,
      inUseTransactions: false,
    },
  }))
  .handleAction(derivativesActions.actionErrorGetDerivativesInUsePositions, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      inUseTransactions: false,
    },
  }))
  .handleAction(derivativesActions.actionClearInUseTransactions, (state) => ({
    ...state,
    inUseTransactions: {
      lastSettlementCurrency: '',
      orders: [],
      positions: [],
    },
  }))
  .handleAction(derivativesActions.actionSetDerivativeOrderIsEditing, (state, action) => ({
    ...state,
    orderIsEditing: action.payload,
  }));
